import { computed, ComputedRef, ref, Ref, watch } from "vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { orderBy as _orderBy, uniqBy as _uniqBy } from "lodash-es";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { BetData, BetsDataWithPaginationToken, BetType } from "@/shared/model/bet";
import { TabValue } from "@/shared/model/tab-group/tab-group";
import { useAuthStore } from "@/shared/stores/use-auth-store";
import { waitUntilTrue } from "@/shared/utils/wait";
import { logException } from "@/shared/utils/logger/use-app-logger";
import PQueue from "p-queue";

const BETS_PER_PAGE = 20; // Default number of bets per page... lower this number (for example to original 20) if response will be large and will cause performance issues

// Used simplified logic... only BETS_PER_PAGE will be used for evaluation so once 'count' will reach BETS_PER_PAGE it will be marked as `${BETS_PER_PAGE}+`.
// In case there will be request for differentiation between `${BETS_PER_PAGE}` and `${BETS_PER_PAGE}+` paginationTokens needs to be taken into evaluation.
const getCountText = (count: number) => (count || "") + (count && count % BETS_PER_PAGE === 0 ? "+" : "");

export const useBetsStore = defineStore("Bets", () => {
    const authStore = useAuthStore();
    const { getBets } = useOpenbetApi();
    // state
    const initialized: Ref<boolean> = ref(false);
    const fetchInterval: Ref<number> = ref(0);
    const activeTab: Ref<BetType> = ref(BetType.OPEN);
    const openBets: Ref<BetData[]> = ref([]);
    const settledBets: Ref<BetData[]> = ref([]);
    const cashoutBets: Ref<BetData[]> = ref([]);
    const openBetsPaginationToken: Ref<string[]> = ref([]);
    const settledBetsPaginationToken: Ref<string[]> = ref([]);
    const cashoutBetsPaginationToken: Ref<string[]> = ref([]);

    // getters
    const allBets: ComputedRef<BetData[]> = computed(() => [
        ...openBets.value,
        ...settledBets.value,
        ...cashoutBets.value,
    ]);
    const openBetsCount: ComputedRef<number> = computed(() => openBets.value?.length || 0);
    const settledBetsCount: ComputedRef<number> = computed(() => settledBets.value?.length || 0);
    const cashoutBetsCount: ComputedRef<number> = computed(() => cashoutBets.value?.length || 0);
    const openBetsCountText: ComputedRef<string> = computed(() => getCountText(openBetsCount.value));
    const settledBetsCountText: ComputedRef<string> = computed(() => getCountText(settledBetsCount.value));
    const cashoutBetsCountText: ComputedRef<string> = computed(() => getCountText(cashoutBetsCount.value));
    const tabs: ComputedRef<TabValue[]> = computed(() => [
        { text: `Otevřené (${openBetsCountText.value || 0})`, value: BetType.OPEN },
        { text: "Vyhodnocené", value: BetType.SETTLED },
        {
            text: `Cashout (${cashoutBetsCountText.value || 0})`,
            value: BetType.CASHOUT,
            disabled: !cashoutBetsCount.value,
        },
    ]);

    const fetchBetsQueue = new PQueue({ concurrency: 1 });

    // actions
    const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
        await waitUntilTrue(initialized, time, timeout).catch(() => {
            logException("bets", undefined, {
                message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
            });
        });
    };
    const setFetchInterval = (interval: number) => {
        fetchInterval.value = interval;
    };
    const setActiveTab = (tab: BetType) => {
        activeTab.value = tab;
    };
    const fetchMoreBets = async (tab: BetType) => {
        const promises: Promise<BetsDataWithPaginationToken>[] = [];
        switch (tab) {
            case BetType.OPEN:
                if (openBetsPaginationToken.value?.length) {
                    openBetsPaginationToken.value.slice(-1).forEach((t) => promises.push(fetchOpenBets(t)));
                }
                break;
            case BetType.SETTLED:
                if (settledBetsPaginationToken.value?.length) {
                    settledBetsPaginationToken.value.slice(-1).forEach((t) => promises.push(fetchSettledBets(t)));
                }
                break;
            case BetType.CASHOUT:
                if (cashoutBetsPaginationToken.value?.length) {
                    cashoutBetsPaginationToken.value.slice(-1).forEach((t) => promises.push(fetchCashoutBets(t)));
                }
                break;
        }
        const results = await Promise.allSettled(promises);
        let retBets: BetData[] = [];
        results.forEach((result) => {
            if (result.status === "fulfilled") {
                retBets = retBets.concat(
                    (result as PromiseFulfilledResult<BetsDataWithPaginationToken>).value?.data || [],
                );
            }
        });
        switch (tab) {
            case BetType.OPEN:
                openBets.value = _orderBy(_uniqBy([...retBets, ...openBets.value], "id"), "placedDate", "desc");
                break;
            case BetType.SETTLED:
                settledBets.value = _orderBy(_uniqBy([...retBets, ...settledBets.value], "id"), "placedDate", "desc");
                break;
            case BetType.CASHOUT:
                cashoutBets.value = _orderBy(_uniqBy([...retBets, ...cashoutBets.value], "id"), "placedDate", "desc");
                break;
        }
    };
    const fetchOpenBets = async (paginationToken?: string, refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        const response = (await fetchBetsQueue.add(
            async () =>
                await getBets({ paginationToken, count: BETS_PER_PAGE, settled: false }, { wsData: { update: true } }),
        )) as BetsDataWithPaginationToken;
        if (response?.paginationToken && !refresh) {
            openBetsPaginationToken.value = [
                ...new Set([...(openBetsPaginationToken.value || []), response.paginationToken]),
            ];
        }
        return response;
    };
    const fetchOpenBetsBatch = async (refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        let retBets: BetData[] = [];
        const promisesOpenBets: Promise<BetsDataWithPaginationToken>[] = [];
        promisesOpenBets.push(fetchOpenBets(undefined, refresh));
        if (openBetsPaginationToken.value?.length) {
            (refresh ? openBetsPaginationToken.value.slice(0, -1) : openBetsPaginationToken.value).forEach((t) =>
                promisesOpenBets.push(fetchOpenBets(t, refresh)),
            );
        }
        const resultsOpenBets = await Promise.allSettled(promisesOpenBets);
        resultsOpenBets.forEach((result) => {
            if (result.status === "fulfilled") {
                retBets = retBets.concat(
                    (result as PromiseFulfilledResult<BetsDataWithPaginationToken>).value?.data || [],
                );
            }
        });
        return retBets;
    };
    const fetchSettledBets = async (paginationToken?: string, refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        const response = (await fetchBetsQueue.add(
            async () =>
                await getBets({ paginationToken, count: BETS_PER_PAGE, settled: true }, { wsData: { update: true } }),
        )) as BetsDataWithPaginationToken;
        if (response?.paginationToken && !refresh) {
            settledBetsPaginationToken.value = [
                ...new Set([...(settledBetsPaginationToken.value || []), response.paginationToken]),
            ];
        }
        return response;
    };
    const fetchSettledBetsBatch = async (refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        let retBets: BetData[] = [];
        const promisesSettledBets: Promise<BetsDataWithPaginationToken>[] = [];
        promisesSettledBets.push(fetchSettledBets(undefined, refresh));
        if (settledBetsPaginationToken.value?.length) {
            (refresh ? settledBetsPaginationToken.value.slice(0, -1) : settledBetsPaginationToken.value).forEach((t) =>
                promisesSettledBets.push(fetchSettledBets(t, refresh)),
            );
        }
        const resultsSettledBets = await Promise.allSettled(promisesSettledBets);
        resultsSettledBets.forEach((result) => {
            if (result.status === "fulfilled") {
                retBets = retBets.concat(
                    (result as PromiseFulfilledResult<BetsDataWithPaginationToken>).value?.data || [],
                );
            }
        });
        return retBets;
    };
    const fetchCashoutBets = async (paginationToken?: string, refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        const response = (await fetchBetsQueue.add(
            async () =>
                await getBets(
                    {
                        paginationToken,
                        count: BETS_PER_PAGE,
                        settled: false,
                        cashoutAvailable: true,
                    },
                    { wsData: { update: true } },
                ),
        )) as BetsDataWithPaginationToken;
        if (response?.paginationToken && !refresh) {
            cashoutBetsPaginationToken.value = [
                ...new Set([...(cashoutBetsPaginationToken.value || []), response.paginationToken]),
            ];
        }
        return response;
    };
    const fetchCashoutBetsBatch = async (refresh: boolean = false) => {
        await authStore.waitUntilTokenAvailable();
        let retBets: BetData[] = [];
        const promisesCashoutBets: Promise<BetsDataWithPaginationToken>[] = [];
        promisesCashoutBets.push(fetchCashoutBets(undefined, refresh));
        if (cashoutBetsPaginationToken.value?.length) {
            (refresh ? cashoutBetsPaginationToken.value.slice(0, -1) : cashoutBetsPaginationToken.value).forEach((t) =>
                promisesCashoutBets.push(fetchCashoutBets(t, refresh)),
            );
        }
        const resultsCashoutBets = await Promise.allSettled(promisesCashoutBets);
        resultsCashoutBets.forEach((result) => {
            if (result.status === "fulfilled") {
                retBets = retBets.concat(
                    (result as PromiseFulfilledResult<BetsDataWithPaginationToken>).value?.data || [],
                );
            }
        });
        return retBets;
    };

    const fetchAllBets = async (refresh: boolean = false) => {
        if (!authStore.isLoggedIn) return;

        const newOpenBets = await fetchOpenBetsBatch(refresh);
        openBets.value = _orderBy(newOpenBets, "placedDate", "desc");
        const newSettledBets = await fetchSettledBetsBatch(refresh);
        settledBets.value = _orderBy(newSettledBets, "placedDate", "desc");
        const newCashoutBets = await fetchCashoutBetsBatch(refresh);
        cashoutBets.value = _orderBy(newCashoutBets, "placedDate", "desc");
    };

    // additional logic
    let fetchIntervalId = null;
    watch(
        fetchInterval,
        (newValue) => {
            if (newValue) {
                fetchIntervalId = setInterval(async () => {
                    await fetchAllBets(true);
                }, fetchInterval.value);
            } else {
                if (fetchIntervalId) {
                    clearInterval(fetchIntervalId);
                }
            }
        },
        { immediate: true },
    );
    watch(
        () => authStore.isLoggedIn,
        (newValue) => {
            if (newValue) {
                // Fetch data immediately once the store is initialized and fetch them in given interval
                fetchAllBets().finally(() => {
                    initialized.value = true;
                });
                setFetchInterval(60_000);
            } else {
                fetchInterval.value = 0;
                activeTab.value = BetType.OPEN;
                openBets.value = [];
                settledBets.value = [];
                cashoutBets.value = [];
                openBetsPaginationToken.value = undefined;
                settledBetsPaginationToken.value = undefined;
                cashoutBetsPaginationToken.value = undefined;
            }
        },
        { immediate: true },
    );

    return {
        initialized,
        fetchInterval,
        activeTab,
        openBets,
        settledBets,
        cashoutBets,
        allBets,
        openBetsCount,
        settledBetsCount,
        cashoutBetsCount,
        openBetsCountText,
        settledBetsCountText,
        cashoutBetsCountText,
        tabs,
        waitUntilInitialized,
        setFetchInterval,
        setActiveTab,
        fetchMoreBets,
        fetchOpenBets,
        fetchOpenBetsBatch,
        fetchSettledBets,
        fetchSettledBetsBatch,
        fetchCashoutBets,
        fetchCashoutBetsBatch,
        fetchAllBets,
    };
});

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useBetsStore, import.meta.hot));
}
